import type {
  ChartBinding,
  ChartBindingValue,
  ChartCategoryColors,
  ChartRule,
  ChartRuleColumn,
  ChartValueColorRule,
  CustomChartBarOrder,
  CustomChartBarOrientation,
  CustomChartDefinition,
  CustomChartSize,
  CustomChartType,
  DashboardPreset,
} from '@/entities/dashboards';
import { RECOMMENDED_CHART_SIZES } from '@/entities/dashboards';

/**
 * Диаграмма правящегося дашборда.
 *
 * Кроме полей самой диаграммы состав держит истолкованное правило и описание, по которому оно
 * получено: по их расхождению видно, что описание правили и правило нужно перестроить. Своё имя
 * поля идентификатора (`chartId`) — чтобы не путать его с `id` готовой диаграммы дашборда.
 */
export type PresetChartFormValues = {
  /** Идентификатор диаграммы: заводится при добавлении и правки переживает */
  chartId: string;
  title: string;
  chartType: CustomChartType;
  size: CustomChartSize;
  /** Высота, заданная растягиванием на сетке; `null` — высота подбирается под содержимое */
  height: number | null;
  /** Диаграмма начинает собой строку сетки, даже когда в предыдущей осталась незанятая ширина */
  startsRow: boolean;
  /** Описание нужных данных на естественном языке */
  description: string;
  /** Правило, закреплённое за описанием; `null` — описание ещё не истолковано */
  rule: ChartRule | null;
  /** Отметка бэкенда о проверенном запросе правила */
  signature: string;
  /** Описание, по которому получено правило; разошлось с `description` — правило устарело */
  resolvedDescription: string;
  /**
   * Тип диаграммы, для которого правило уже проверено. Разошёлся с `chartType` — тип сменили
   * после истолкования, и соответствие правила новому типу ещё предстоит проверить.
   */
  resolvedChartType: CustomChartType;
  /** Пересказ правила на русском — показывается пользователю на подтверждение */
  summary: string;
  /**
   * Значения выборки правила — из них задаются подстановки текста результата. Пусто, пока
   * правило в этот заход не исполнялось: тогда перечень берётся из наполнения диаграммы.
   */
  columns: ChartRuleColumn[];
  /** Места подстановки, которых ждёт запрос правила: разрез диаграммы и выбранное в ведущей */
  bindings: ChartBinding[];
  /** Начальное значение разреза; `null` — разрез начинается неопределённым */
  sliceValue: ChartBindingValue;
  /** Ведущая диаграмма, выбор в которой наполняет эту; `null` — связи нет */
  sourceChartId: string | null;
  /** Направление элементов столбчатой диаграммы */
  barOrientation: CustomChartBarOrientation;
  /** Порядок элементов столбчатой диаграммы; `null` — элементы идут, как дала выборка */
  barOrder: CustomChartBarOrder | null;
  /** Цвета, заданные категориям поимённо; пусто — все категории красятся набором */
  categoryColors: ChartCategoryColors;
  /** Правила раскраски по величине в порядке проверки; пусто — раскраски по величине нет */
  valueColorRules: ChartValueColorRule[];
  /** Окрашиваемые колонки таблицы; пусто — правила красят все числовые колонки */
  coloredColumns: string[];
};

/**
 * Оформление диаграммы: то, чем она нарисована.
 *
 * Правится в свойствах диаграммы и применяется к ней сразу, не дожидаясь сохранения свойств, —
 * как и размер: пользователь выбирает цвет, направление и порядок, глядя на саму диаграмму.
 */
export type ChartAppearanceValues = Pick<PresetChartFormValues, ChartAppearanceKey>;

/** Поля оформления диаграммы — одним перечнем у формы, у состава дашборда и у представления. */
export type ChartAppearanceKey =
  | 'barOrientation'
  | 'barOrder'
  | 'categoryColors'
  | 'valueColorRules'
  | 'coloredColumns';

/** Оформление диаграммы — её поля оформления без правила, размера и прочего состава. */
export const pickChartAppearance = <TChart extends Partial<Record<ChartAppearanceKey, unknown>>>(
  chart: TChart,
): Pick<TChart, ChartAppearanceKey> => ({
  barOrientation: chart.barOrientation,
  barOrder: chart.barOrder,
  categoryColors: chart.categoryColors,
  valueColorRules: chart.valueColorRules,
  coloredColumns: chart.coloredColumns,
});

/** Направление элементов, которым столбчатая диаграмма показана, пока пользователь не выбрал иного. */
export const DEFAULT_BAR_ORIENTATION: CustomChartBarOrientation = 'horizontal';

/**
 * Правящееся правило диаграммы: сам запрос и текст его результата.
 *
 * Держится отдельно от значений формы: правилами формы запрос и текст не проверяются, а
 * сохраняются иначе, чем описание, — заверением либо вовсе без обращения к бэкенду.
 */
export type ChartRuleDraft = {
  query: string;
  template: string;
};

/** Правящийся состав пользовательского дашборда: его название и диаграммы. */
export type DashboardPresetFormValues = {
  name: string;
  charts: PresetChartFormValues[];
};

/** Тип диаграммы, предлагаемый новой диаграмме: самый простой из каталога. */
const DEFAULT_CHART_TYPE: CustomChartType = 'metric';

/** Размер новой диаграммы — первый рекомендуемый для её типа. */
export const defaultSizeForType = (chartType: CustomChartType): CustomChartSize =>
  RECOMMENDED_CHART_SIZES[chartType][0];

/**
 * Пустая диаграмма для добавления в состав дашборда: название, тип и размер ей даст
 * истолкование описания, а до него заготовка держит их значениями каталога. Начало строки
 * приходит от места, откуда диаграмму заводят: место в конце сетки ставит её следующей строкой.
 */
export const createEmptyChart = (startsRow: boolean): PresetChartFormValues => ({
  chartId: crypto.randomUUID(),
  title: '',
  chartType: DEFAULT_CHART_TYPE,
  size: defaultSizeForType(DEFAULT_CHART_TYPE),
  height: null,
  startsRow,
  description: '',
  rule: null,
  signature: '',
  resolvedDescription: '',
  resolvedChartType: DEFAULT_CHART_TYPE,
  summary: '',
  columns: [],
  bindings: [],
  sliceValue: null,
  sourceChartId: null,
  barOrientation: DEFAULT_BAR_ORIENTATION,
  barOrder: null,
  categoryColors: {},
  valueColorRules: [],
  coloredColumns: [],
});

/**
 * Раскладывает пресет в правящийся состав. Правило считается действующим для сохранённого
 * описания — правка описания сама сделает его устаревшим. Нового дашборда правка начинается
 * с пустой сетки: диаграммы пользователь добавляет на ней сам.
 */
export const toFormValues = (preset: DashboardPreset | null): DashboardPresetFormValues => {
  if (!preset) return { name: '', charts: [] };
  return {
    name: preset.name,
    charts: preset.charts.map((chart) => ({
      chartId: chart.id,
      title: chart.title,
      chartType: chart.chartType,
      size: chart.size,
      height: chart.height ?? null,
      startsRow: chart.startsRow ?? false,
      description: chart.description,
      rule: chart.rule,
      // Пресет приходит из настроек пользователя и мог быть записан прежним составом
      // диаграммы: у записанной до заверения диаграммы отметки нет.
      signature: chart.signature ?? '',
      resolvedDescription: chart.description,
      resolvedChartType: chart.chartType,
      summary: '',
      // Правило сохранённой диаграммы через истолкование не проходит — значения её выборки
      // становятся известны с первого наполнения.
      columns: [],
      bindings: chart.bindings ?? [],
      sliceValue: chart.sliceValue ?? null,
      sourceChartId: chart.sourceChartId ?? null,
      barOrientation: chart.barOrientation ?? DEFAULT_BAR_ORIENTATION,
      barOrder: chart.barOrder ?? null,
      categoryColors: chart.categoryColors ?? {},
      valueColorRules: chart.valueColorRules ?? [],
      coloredColumns: chart.coloredColumns ?? [],
    })),
  };
};

/**
 * Правило с текстом результата `template`: пустой текст снимает его с правила. Текст подписи правила
 * не меняет и заверения не требует.
 */
export const withChartTemplate = (rule: ChartRule, template: string | undefined): ChartRule => {
  const text = template?.trim();
  return text ? { ...rule, template: text } : { query: rule.query };
};

/** Диаграмма правящегося состава с уже закреплённым правилом — её и показывает дашборд. */
export type ResolvedPresetChart = PresetChartFormValues & { rule: ChartRule };

/** Правило за диаграммой закреплено — её есть чем наполнить. */
export const isResolvedChart = (chart: PresetChartFormValues): chart is ResolvedPresetChart => chart.rule !== null;

/** Собирает диаграмму дашборда из её правящихся значений. */
export const toChartDefinition = (chart: ResolvedPresetChart): CustomChartDefinition => ({
  id: chart.chartId,
  title: chart.title.trim(),
  chartType: chart.chartType,
  size: chart.size,
  ...(chart.height === null ? {} : { height: chart.height }),
  ...(chart.startsRow ? { startsRow: true } : {}),
  description: chart.description.trim(),
  rule: chart.rule,
  signature: chart.signature,
  ...(chart.bindings.length > 0 ? { bindings: chart.bindings } : {}),
  ...(chart.sliceValue === null ? {} : { sliceValue: chart.sliceValue }),
  ...(chart.sourceChartId ? { sourceChartId: chart.sourceChartId } : {}),
  // Оформление пишется в состав только заданное: пустые значения сделали бы прежний дашборд
  // правленым на одном входе в режим редактирования — состав сравнивается со записанным.
  ...(chart.barOrientation === DEFAULT_BAR_ORIENTATION ? {} : { barOrientation: chart.barOrientation }),
  ...(chart.barOrder ? { barOrder: chart.barOrder } : {}),
  ...(Object.keys(chart.categoryColors).length > 0 ? { categoryColors: chart.categoryColors } : {}),
  ...(chart.valueColorRules.length > 0 ? { valueColorRules: chart.valueColorRules } : {}),
  ...(chart.coloredColumns.length > 0 ? { coloredColumns: chart.coloredColumns } : {}),
});

/**
 * Собирает состав дашборда из правящихся значений. Диаграммы без правила в состав не попадают:
 * описание каждой истолковано ещё в её свойствах.
 */
export const toChartDefinitions = (charts: PresetChartFormValues[]): CustomChartDefinition[] =>
  charts.filter(isResolvedChart).map(toChartDefinition);
